"use client";

// ── Единый «живой» drag портала ─────────────────────────────────────────────
// Одно ядро тач-перетаскивания для ВСЕХ мест, где что-то тащат/переупорядочивают
// (канбан-доски, реордер списков, перенос карточек между колонками, реордер
// колонок таблиц). Извлечено 1:1 из проверенного канбана «Логистика рейсов»
// (rd-collection), обобщено под любой источник/приёмник через селекторы и
// data-атрибуты. Без библиотек — тот же почерк, что у .glow-pill / .yuc-pop.
//
// ПОЧЕМУ вообще нужно: нативный HTML5 drag (draggable/onDrag*) на тач-экранах
// НЕ работает — событий dragstart/drop там нет, пальцем перетащить нельзя.
// Поэтому для указателей touch/pen делаем свой long-press-drag на Pointer Events:
//   • нажал и держишь ~250 мс на месте → включается перетаскивание («призрак»-
//     клон под пальцем + лёгкая вибрация);
//   • обычный свайп до срабатывания таймера остаётся скроллом (таймер
//     отменяется при сдвиге > 10px) — прокрутка списков/досок не ломается;
//   • пока тащим — блокируем прокрутку страницы (non-passive touchmove),
//     приёмник под пальцем ищем через elementFromPoint + data-атрибут;
//   • «фантомный» click после drop подавляем (не открывается редактор/карточка).
// Мышь сюда не заходит (pointerType === "mouse" → return) — у неё остаётся
// штатный HTML5-DnD, который на десктопе привычнее и точнее.
//
// Всё видимое движение — на transform/opacity/box-shadow (компоновщик, без
// layout-трэшинга) и гасится под prefers-reduced-motion (см. globals.css,
// классы .dnd-card / .dnd-col / .dnd-ghost / .dnd-drop-hint / .dnd-draggable).

export interface DragCtx {
  /** Координаты указателя в момент события. */
  clientX: number;
  clientY: number;
  /** Ближайший элемент-приёмник под пальцем (match targetSelector) или null. */
  targetEl: HTMLElement | null;
  /** Значение data-атрибута приёмника (ключ колонки / id соседа) или null. */
  targetKey: string | null;
}

export interface StartPointerDragOptions {
  /** CSS-селектор перетаскиваемого элемента — его клон станет «призраком». */
  itemSelector: string;
  /** CSS-селектор элемента-приёмника (напр. "[data-dnd-col]"). */
  targetSelector: string;
  /** Имя data-атрибута приёмника, читаемого как ключ (напр. "data-dnd-col"). */
  targetAttr: string;
  /** Порог long-press до старта перетаскивания, мс (по умолчанию 250). */
  longPressMs?: number;
  /** «Призрак» слегка наклоняется (уместно для карточек, не для th). */
  ghostRotate?: boolean;
  /** Старт перетаскивания: выставить состояние «тащим» (для .is-dragging). */
  onStart?: () => void;
  /** Указатель над приёмником сменился: подсветить цель. */
  onOver?: (ctx: DragCtx) => void;
  /** Отпустили над приёмником: закоммитить перемещение. */
  onDrop?: (ctx: DragCtx) => void;
  /** Всегда по завершении (drop/cancel): сбросить состояние. */
  onEnd?: () => void;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Запустить тач/pen long-press-drag. Вызывать из onPointerDown перетаскиваемого
 * элемента; мышь игнорируется (у неё остаётся нативный HTML5-DnD). Все id/ключи
 * замыкаются в переданных колбэках — ядро само их не хранит.
 */
export function startPointerDrag(e: React.PointerEvent, opts: StartPointerDragOptions): void {
  if (e.pointerType === "mouse") return;
  const itemEl = (e.currentTarget as HTMLElement).closest<HTMLElement>(opts.itemSelector)
    ?? (e.currentTarget as HTMLElement);
  if (!itemEl) return;

  const {
    targetSelector,
    targetAttr,
    longPressMs = 250,
    ghostRotate = true,
    onStart,
    onOver,
    onDrop,
    onEnd,
  } = opts;

  const reduced = prefersReducedMotion();
  const pointerId = e.pointerId;
  const startX = e.clientX;
  const startY = e.clientY;
  let active = false;
  let ghost: HTMLElement | null = null;
  let ghostOffX = 0;
  let ghostOffY = 0;
  let lastKey: string | null = null;
  let lastCtx: DragCtx = { clientX: startX, clientY: startY, targetEl: null, targetKey: null };

  const blockScroll = (ev: TouchEvent) => { if (active) ev.preventDefault(); };

  const resolve = (x: number, y: number): DragCtx => {
    const under = document.elementFromPoint(x, y);
    const targetEl = under?.closest<HTMLElement>(targetSelector) ?? null;
    const targetKey = targetEl?.getAttribute(targetAttr) ?? null;
    return { clientX: x, clientY: y, targetEl, targetKey };
  };

  const beginDrag = () => {
    active = true;
    onStart?.();
    try { navigator.vibrate?.(12); } catch { /* не поддерживается — не страшно */ }

    const rect = itemEl.getBoundingClientRect();
    ghostOffX = startX - rect.left;
    ghostOffY = startY - rect.top;
    ghost = itemEl.cloneNode(true) as HTMLElement;
    ghost.classList.add("dnd-ghost");
    Object.assign(ghost.style, {
      position: "fixed",
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      margin: "0",
      pointerEvents: "none",
      zIndex: "9999",
      opacity: "0.95",
      transform: reduced ? "none" : `scale(1.03)${ghostRotate ? " rotate(1.2deg)" : ""}`,
      boxShadow: "0 16px 34px rgba(0,0,0,0.34)",
      transition: "none",
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(ghost);
    document.body.style.userSelect = "none";
    // Блокируем скролл только на время активного перетаскивания.
    itemEl.style.touchAction = "none";
    itemEl.classList.add("is-dragging");
    document.addEventListener("touchmove", blockScroll, { passive: false });
  };

  let timer: ReturnType<typeof setTimeout> | null = setTimeout(beginDrag, longPressMs);

  const cleanup = () => {
    if (timer) { clearTimeout(timer); timer = null; }
    document.removeEventListener("pointermove", onMove);
    document.removeEventListener("pointerup", onUp);
    document.removeEventListener("pointercancel", onCancel);
    document.removeEventListener("touchmove", blockScroll);
    if (ghost) { ghost.remove(); ghost = null; }
    document.body.style.removeProperty("user-select");
    itemEl.style.removeProperty("touch-action");
    itemEl.classList.remove("is-dragging");
  };

  const onMove = (ev: PointerEvent) => {
    if (ev.pointerId !== pointerId) return;
    const dx = ev.clientX - startX;
    const dy = ev.clientY - startY;
    if (!active) {
      // Сдвинулись раньше, чем сработал long-press → это скролл/промах, отменяем.
      if (Math.hypot(dx, dy) > 10) cleanup();
      return;
    }
    ev.preventDefault();
    if (ghost) {
      ghost.style.left = `${ev.clientX - ghostOffX}px`;
      ghost.style.top = `${ev.clientY - ghostOffY}px`;
    }
    const ctx = resolve(ev.clientX, ev.clientY);
    lastCtx = ctx;
    if (ctx.targetKey !== lastKey) {
      lastKey = ctx.targetKey;
      onOver?.(ctx);
    }
  };

  const onUp = (ev: PointerEvent) => {
    if (ev.pointerId !== pointerId) return;
    const wasActive = active;
    const dropCtx = lastCtx;
    cleanup();
    if (wasActive) {
      // После перетаскивания подавляем «фантомный» click, чтобы не открылся редактор.
      const suppress = (ce: Event) => {
        ce.stopPropagation();
        ce.preventDefault();
        window.removeEventListener("click", suppress, true);
      };
      window.addEventListener("click", suppress, true);
      setTimeout(() => window.removeEventListener("click", suppress, true), 500);
      onDrop?.(dropCtx);
      onEnd?.();
    }
  };

  const onCancel = (ev: PointerEvent) => {
    if (ev.pointerId !== pointerId) return;
    const wasActive = active;
    cleanup();
    if (wasActive) onEnd?.();
  };

  document.addEventListener("pointermove", onMove);
  document.addEventListener("pointerup", onUp);
  document.addEventListener("pointercancel", onCancel);
}
